import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { User, UserManager } from 'oidc-client';

import * as Wizard from './Wizard';
import jyuLogo from './jyu-logo.png';
import logo from './logo.png';
import { AssessmentView } from './assessment';
import * as Auth from './auth';
import * as Q from './questions';

// two different environments, research collects data and has some different text
// from the counselling environment which doesn't collect data and is always open
type Environment = 'RESEARCH' | 'COUNSELLING';

//---------------------------------//
// validation and utility elements //
//---------------------------------//

// allow to disable with env var to quickly test server-side features
const vRequired: (val: any) => string | undefined = process.env.REACT_APP_DISABLE_VALIDATION
  ? () => undefined
  : (value) => (value ? undefined : 'Pakollinen tieto');

interface ValidationProps {
  name: Q.FieldName;
  validate: (val: any) => string | undefined;
  vertical?: true;
}

const Validation: React.FC<ValidationProps> = (props) => (
  <Q.Field
    name={props.name}
    subscribe={{ touched: true, error: true }}
    render={({ meta: { touched, error } }) => (
      <div className={`field-container ${props.vertical ? 'vertical' : ''}`}>
        <div className={touched && error ? 'field-inner has-error' : 'field-inner'}>{props.children}</div>
        {touched && error ? <div className="error-msg">{error}</div> : <></>}
      </div>
    )}
    validate={props.validate}
  />
);

interface LikertProps {
  name: Q.LikertName;
}

const Likert: React.FC<LikertProps> = ({ name }) => (
  <div>
    <strong>{Q.allLikerts[name]}</strong>
    <br />
    <br />
    <Validation name={name} validate={vRequired} vertical>
      <div className="columns">
        <div className="column has-text-centered is-gapless" />
        <div className="column has-text-centered is-gapless">
          <label className="radio">
            <Q.Field name={name} component="input" type="radio" value="1" />
            <br />
            <span className="likert">ei lainkaan</span>
          </label>
        </div>
        <div className="column has-text-centered is-gapless">
          <label className="radio">
            <Q.Field name={name} component="input" type="radio" value="2" />
            <br />
            <span className="likert">vähän</span>
          </label>
        </div>
        <div className="column has-text-centered is-gapless">
          <label className="radio">
            <Q.Field name={name} component="input" type="radio" value="3" />
            <br />
            <span className="likert">jonkin verran</span>
          </label>
        </div>
        <div className="column has-text-centered is-gapless">
          <label className="radio">
            <Q.Field name={name} component="input" type="radio" value="4" />
            <br />
            <span className="likert">hyvin</span>
          </label>
        </div>
        <div className="column has-text-centered is-gapless">
          <label className="radio">
            <Q.Field name={name} component="input" type="radio" value="5" />
            <br />
            <span className="likert">erittäin hyvin</span>
          </label>
        </div>
        <div className="column has-text-centered is-gapless"></div>
      </div>
    </Validation>
    <hr />
  </div>
);

const PermissionRadio: React.FC<{ name: Q.FieldName }> = ({ name }) => {
  return (
    <Validation name={name} validate={vRequired} vertical>
      <div className="box has-text-centered">
        <strong>Annan luvan</strong>
        <div className="columns is-centered is-mobile">
          <div className="column has-text-centered is-narrow">
            <label className="radio">
              <Q.Field name={name} component="input" type="radio" value="true" />
              &nbsp;Kyllä
            </label>
          </div>
          <div className="column has-text-centered is-narrow">
            <label className="radio">
              <Q.Field name={name} component="input" type="radio" value="false" />
              &nbsp;En
            </label>
          </div>
        </div>
      </div>
    </Validation>
  );
};

//----------------//
// admin controls //
//----------------//

const MaybeAdminControls: React.FC<{ user: User; stateQuery: Q.StateQueryResp; environment: Environment }> = ({
  user,
  stateQuery,
  environment,
}) => {
  const [excelLoading, setExcelLoading] = useState<boolean>(false);
  const [formIsActive, setFormIsActive] = useState<boolean>(stateQuery.formIsActive);
  const [activateLoading, setActivateLoading] = useState<boolean>(false);
  if (!Auth.userIsAdmin(user)) {
    return <></>;
  }
  if (environment === 'COUNSELLING') {
    // admin controls don't work if the database is not being used
    return <></>;
  }
  return (
    <div className="section">
      <div className="container is-max-desktop">
        <div className="box">
          <p className="block">Olet kirjautunut pääkäyttäjänä.</p>
          <button
            className={`button ${excelLoading ? 'is-loading' : ''}`}
            onClick={async () => {
              if (excelLoading) return;
              setExcelLoading(true);
              try {
                const resp = await axios.get('/api/admin/excel', {
                  headers: Auth.authHeaders(user),
                  responseType: 'blob',
                });
                // downloading with JS is cumbersome
                const url = window.URL.createObjectURL(new Blob([resp.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'taitokartta.xlsx');
                document.body.appendChild(link);
                link.click();
              } catch (e) {
                alert(`Tapahtui virhe: ${e}\nJos tämä toistuu, ota yhteyttä kehittäjiin.`);
              }
              setExcelLoading(false);
            }}
          >
            Lataa Excel
          </button>
          {formIsActive ? (
            <>
              <p className="block mt-4">Kysely on avoinna.</p>
              <button
                className={`button is-danger ${activateLoading ? 'is-loading' : ''}`}
                onClick={async () => {
                  if (activateLoading) return;
                  setActivateLoading(true);
                  try {
                    await axios.post(
                      '/api/admin/deactivate',
                      {},
                      {
                        headers: Auth.authHeaders(user),
                      },
                    );
                    setFormIsActive(false);
                  } catch (e) {
                    alert(`Tapahtui virhe: ${e}\nJos tämä toistuu, ota yhteyttä kehittäjiin.`);
                  }
                  setActivateLoading(false);
                }}
              >
                Sulje kysely
              </button>
            </>
          ) : (
            <>
              <p className="block mt-4">Kysely on suljettu.</p>
              <button
                className={`button is-success ${activateLoading ? 'is-loading' : ''}`}
                onClick={async () => {
                  if (activateLoading) return;
                  setActivateLoading(true);
                  try {
                    await axios.post(
                      '/api/admin/activate',
                      {},
                      {
                        headers: Auth.authHeaders(user),
                      },
                    );
                    setFormIsActive(true);
                  } catch (e) {
                    alert(`Tapahtui virhe: ${e}\nJos tämä toistuu, ota yhteyttä kehittäjiin.`);
                  }
                  setActivateLoading(false);
                }}
              >
                Avaa kysely
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

//-----//
// app //
//-----//

const App = () => {
  const [userMgr, setUserMgr] = useState<UserManager | undefined>(undefined);
  const [initError, setInitError] = useState<string | undefined>(undefined);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | undefined>(undefined);
  // state information queried at login
  const [formState, setFormState] = useState<Q.StateQueryResp | undefined>(undefined);
  // we need to know which environment we're in before login to show a different intro text,
  // therefore query for this immediately at startup
  const [environment, setEnvironment] = useState<Environment | undefined>(undefined);
  const [viewingAnswerIdx, setViewingAnswerIdx] = useState<number | undefined>(undefined);
  const [answeringAgain, setAnsweringAgain] = useState<boolean>(false);
  // undefined == didn't get anything yet, null == got nothing
  const [localStorageVals, setLocalStorageVals] = useState<Q.FormValues | null | undefined>(undefined);
  const [submittedValues, setSubmittedValues] = useState<Q.FormValues | undefined>(undefined);
  const [receivedProfiles, setReceivedProfiles] = useState<string | undefined>(undefined);

  // check login
  useEffect(() => {
    (async () => {
      // get oidc config from server and create user manager from it
      try {
        const uMgr = await Auth.createUserManager();
        setUserMgr(uMgr);
        const envResp = await axios.get<string>('/api/environment');
        setEnvironment(envResp.data === 'COUNSELLING' ? 'COUNSELLING' : 'RESEARCH');
      } catch (e) {
        setInitError(e.toString());
      }
    })();
  }, []);
  useEffect(() => {
    if (!userMgr) return;
    (async () => {
      // sync user to program state with events
      userMgr.events.addUserLoaded((user) => {
        setUser(user);
      });
      userMgr.events.addSilentRenewError(() => {
        alert('Kirjautumisesi on vanhentunut. Ole hyvä ja kirjaudu uudelleen sisään.');
        // alert blocks, so this will only happen once user clicks ok
        userMgr.signinRedirect();
      });
      // check if we're redirecting back from login, get user if so
      // this should be last, otherwise the userLoaded event listener won't be registered in time!
      await Auth.checkSigninRedirect(userMgr, setLoggingIn, setFormState);
    })();
  }, [userMgr]);

  // check localstorage for previous incomplete answer
  useEffect(() => {
    const answers = localStorage.getItem('incompleteAnswers');
    const timestamp = localStorage.getItem('timestamp');
    if (!answers || !timestamp) {
      setLocalStorageVals(null);
      return;
    }

    const LS_MAX_AGE = 1000 * 60 * 30;

    const ageMs = Date.now() - parseInt(timestamp);
    if (ageMs < LS_MAX_AGE) {
      try {
        setLocalStorageVals(JSON.parse(answers));
      } catch {
        setLocalStorageVals(null);
        localStorage.clear();
      }
    } else {
      setLocalStorageVals(null);
      localStorage.clear();
    }
  }, []);

  const onChange = (values: Q.FormValues) => {
    // don't do this if the initial load effect is still running
    if (localStorageVals === undefined) return;

    const personalInfoErased: Q.FormValues = { ...values, userFullName: '', userEmail: '' };
    localStorage.setItem('incompleteAnswers', JSON.stringify(personalInfoErased));
    localStorage.setItem('timestamp', Date.now().toString());
  };

  const onSubmit = async (values: Q.FormValues) => {
    if (!user) return;
    try {
      const profileResp = await axios.post<string>('/api/taitokartta', values, {
        headers: Auth.authHeaders(user),
      });
      if (profileResp.status !== 200) {
        throw new Error(`Server returned an error: ${profileResp.status}`);
      }
      setSubmittedValues(values);
      setReceivedProfiles(profileResp.data);

      localStorage.clear();
    } catch (e) {
      alert(`Vastaustesi tallentamisessa tapahtui virhe. Ole hyvä ja yritä myöhemmin uudelleen. (${e})`);
    }
  };

  //
  // view variants
  //

  // error

  if (initError) {
    return (
      <Layout>
        <div className="container">
          <div className="section">
            <p>Tapahtui virhe. Yritä myöhemmin uudelleen.</p>
            <p>{initError}</p>
          </div>
        </div>
      </Layout>
    );
  }

  // login or environment query processing

  if (loggingIn) {
    return (
      <Layout>
        <div className="container">
          <div className="section">
            <span className="spinner mr-2" />
            <span>Kirjautumista käsitellään...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!environment) {
    return (
      <Layout>
        <div className="container">
          <div className="section">
            <span className="spinner mr-2" />
            <span>Ladataan...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // intro page (not logged in)

  if (!user) {
    if (!userMgr) {
      return <Layout />;
    }
    if (environment === 'COUNSELLING') {
      return <IntroductionPageCounselling loginAction={() => userMgr.signinRedirect()} />;
    }
    return <IntroductionPageResearch loginAction={() => userMgr.signinRedirect()} />;
  }

  // previous answers view

  if (formState !== undefined && !answeringAgain) {
    if (formState.answers.length > 0) {
      const fmtDate = (d: Date) =>
        d.toLocaleDateString('fi-FI', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        });
      if (viewingAnswerIdx === undefined) {
        // previous answers list
        return (
          <Layout>
            <MaybeAdminControls user={user} stateQuery={formState} environment={environment} />
            <div className="container">
              <div className="section">
                <p className="block">Olet jo vastannut kyselyyn. Tästä voit palata aikaisempiin vastauksiisi.</p>
                <div className="content">
                  <ul>
                    {formState.answers.map((ans, idx) => {
                      return (
                        <li key={ans.timestamp.toString()}>
                          <button className="button is-text" onClick={() => setViewingAnswerIdx(idx)}>
                            Vastauksesi {fmtDate(ans.timestamp)}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              {formState.permissionToAnswer && (
                <div className="section">
                  <h2 className="subtitle">Vastaa uudestaan</h2>
                  <p className="block">
                    Edellisestä vastauksestasi on kulunut tarpeeksi kauan, että voit vastata kyselyyn uudestaan.
                  </p>
                  <button className="button is-text" onClick={() => setAnsweringAgain(true)}>
                    Vastaa uudelleen tästä
                  </button>
                </div>
              )}
            </div>
          </Layout>
        );
      } else {
        // specific previous answer
        const ans = formState.answers[viewingAnswerIdx];
        return (
          <Layout>
            <div className="container">
              <div className="section">
                <div className="is-flex is-justify-content-space-between">
                  <h2 className="title">Vastauksesi {fmtDate(ans.timestamp)}</h2>
                  <button
                    className="delete is-large is-align-self-center"
                    title="Sulje"
                    onClick={() => setViewingAnswerIdx(undefined)}
                  />
                </div>
                <AssessmentView formValues={ans.values} profiles_html={ans.profiles} />
              </div>
            </div>
          </Layout>
        );
      }
    } else if (!formState.permissionToAnswer) {
      // no answers and no permission to answer
      return (
        <Layout>
          <MaybeAdminControls user={user} stateQuery={formState} environment={environment} />
          <div className="container">
            <div className="section">
              <p className="block">Kysely on suljettu.</p>
            </div>
          </div>
        </Layout>
      );
    }
  }

  // final assessment after answering

  if (submittedValues && receivedProfiles) {
    return (
      <Layout>
        <section className="section">
          <div className="container">
            <h2 className="title">Kiitos vastauksistasi!</h2>
            <AssessmentView formValues={submittedValues} profiles_html={receivedProfiles} />
          </div>
        </section>
      </Layout>
    );
  }

  // main form (logged in)

  const initialValues = localStorageVals || Q.initialValues;
  initialValues.userFullName = user.profile.name || '';
  initialValues.userEmail = user.profile.email || '';
  if (formState === undefined) {
    // still loading the form state, this shouldn't take long so just wait
    return <>loading</>;
  }

  return (
    <MainForm
      environment={environment}
      formState={formState}
      initialValues={initialValues}
      onSubmit={onSubmit}
      onChange={onChange}
      user={user}
    />
  );
};

const Layout: React.FC<{}> = ({ children }) => (
  <div className="top-layer-container d-flex flex-column">
    <section className="hero is-primary">
      <div className="hero-body">
        <div className="container">
          <div className="columns">
            <div className="column is-1">
              <img src={jyuLogo} alt="Jyu logo" width={30} />
            </div>
            <div className="column">
              <h1 className="title">Opiskelun Taitokartta</h1>
              <div className="subtitle">Jyväskylän yliopisto</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {children}

    <footer className="footer mt-auto">
      <div className="has-text-centered is-size-7">
        <p className="block">&copy; Jyväskylän yliopisto 2019-{new Date().getFullYear()}</p>
      </div>
    </footer>
  </div>
);

interface IntroProps {
  loginAction: () => void;
}

const IntroductionPageResearch: React.FC<IntroProps> = (props) => (
  <Layout>
    <div className="section">
      <div className="container">
        <h2 className="title is-4">Tervetuloa vastaamaan Opiskelun Taitokartan itsearviointiin!</h2>
        <div className="is-flex front-page-columns">
          <div className="column">
            <p className="block">
              Opiskelun Taitokartta on Jyväskylän yliopistossa kehitetty internet-pohjainen akateemisten
              opiskelutaitojen itsearviointikysely opiskelijoille. Se kohdennetaan opintojen alkuvaiheessa ensimmäisen
              vuosikurssin opiskelijoille sekä vain maisteriopintojen suorittamisen aloittaneille opiskelijoille.
            </p>

            <p className="block">
              Opiskelun Taitokartan itsearvioinnissa sinä arvioit omia lukemisen, kirjoittamisen ja luetun ymmärtämisen
              perustaitojasi sekä opiskelun hallintaasi. Itsearviointisi tuloksista saat automaattisena palautteena omat
              taitoprofiilisi. Taitoprofiili kertoo sinulle jäsennetysti akateemisten opiskelutaitojesi vahvuuksia ja
              kehittämistarpeita. Halutessasi pääset myös vertaamaan omia taitoprofiilejasi kaikkien
              itsearviointikyselyyn vastanneiden arvioihin.
            </p>

            <p className="block">
              Opiskelun Taitokarttaa hyödynnetään myös yliopiston ohjauksen, opetuksen ja tutkimuksen kehittämisessä.
              Ohjauksen ja opetuksen kehittämistä varten ryhmänohjaajille ja koulutussuunnittelijoille annetaan kooste
              taitoprofiileista tiedekunnittain/laitoksittain, mutta ei yksittäisten opiskelijoiden alkuperäisiä
              vastauksia, eikä myöskään mitään tunnistetietoja.
            </p>

            <p className="block">
              Kaikki analyysit tehdään luottamuksellisesti henkilötietoja paljastamatta, tuloksia raportoitaessa
              pidetään huoli siitä, ettei yksittäistä vastaajaa ole mahdollista tunnistaa. Antamasi tiedot ovat käytössä
              vain niihin tarkoituksiin, joihin Sinä itse annat suostumuksen Opiskelun Taitokartta -kyselyn alussa
              olevassa suostumus tutkimukseen -kyselyssä.
            </p>

            <p className="block">
              Pyydämme sinua vastaamaan itsearviointiin, joka on avoinna kuukauden ajan. Opiskelun Taitokarttaan
              vastaamiseen tarvitaan aikaa vain 10–15 minuuttia.
            </p>

            <p className="block">
              Opiskelun Taitokartan tutkimuksesta on tehty tieteellisen tutkimuksen tietosuojaseloste, josta ilmenevät
              tiedot tutkimusrekisterin rekisterinpitäjästä, henkilötietojen käsittelijöistä ja tietojen suojaamisesta.
              Voit tutustua tietosuojaselosteeseen täältä:{' '}
              <a href="https://www.jyu.fi/fi/palvelut/studentlife/sl-opiskelijalle/opiskelun-taitokartta/tietosuojailmoitus_opiskelun_taitokartta.pdf">
                Tietosuojaselosteeseen
              </a>
            </p>

            <p className="block">
              Opiskelun Taitokartta -kyselyyn vastaaminen auttaa sinua tunnistamaan akateemisten opiskelutaitojesi
              vahvuuksia ja kehittämistarpeita, mutta{' '}
              <b>taitoprofiilikuvauksen saat vasta tietojen lähettämisen jälkeen.</b> Opiskelun Taitokarttaan pääset
              vastaamaan kirjautumalla kyselyyn opiskelijatunnuksillasi.{' '}
              <b>Huomioi, että vastaustesi tiedot tallentuvat vasta lähetä-napin painalluksen jälkeen.</b>
            </p>
            <button type="button" onClick={props.loginAction}>
              Kirjaudu tästä
            </button>
          </div>
          <div className="column is-3">
            <img src={logo} alt="Opiskelun Taitokartan logo" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
);

const IntroductionPageCounselling: React.FC<IntroProps> = (props) => (
  <Layout>
    <div className="section">
      <div className="container">
        <h2 className="title is-4">Tervetuloa vastaamaan Opiskelun Taitokartan itsearviointiin!</h2>

        <div className="is-flex front-page-columns">
          <div className="column">
            <p className="block">
              Opiskelun Taitokartta on Jyväskylän yliopistossa kehitetty internet-pohjainen akateemisten
              opiskelutaitojen itsearviointikysely opiskelijoille.
            </p>

            <p className="block">
              Opiskelun Taitokartan itsearvioinnissa sinä arvioit omia lukemisen, kirjoittamisen ja luetun ymmärtämisen
              perustaitojasi sekä opiskelun hallintaasi. Itsearviointisi tuloksista saat automaattisena palautteena omat
              taitoprofiilisi. Taitoprofiili kertoo sinulle jäsennetysti akateemisten opiskelutaitojesi vahvuuksia ja
              kehittämistarpeita. Halutessasi pääset myös vertaamaan omia taitoprofiilejasi kaikkien
              itsearviointikyselyyn vastanneiden arvioihin Student Lifen sivuilla. Tämän Opiskelun Taitokartta -version
              vastauksia ei tallenneta eikä niitä käytetä tutkimuskäyttöön.
            </p>

            <p className="block">
              Opiskelun Taitokarttaan pääset vastaamaan kirjautumalla kyselyyn opiskelijatunnuksillasi ja vastaamiseen
              tarvitaan aikaa vain 10–15 minuuttia.
            </p>
            <button type="button" onClick={props.loginAction}>
              Kirjaudu tästä
            </button>
          </div>
          <div className="column is-3">
            <img src={logo} alt="Opiskelun taitokartan logo" />
          </div>
        </div>
      </div>
    </div>
  </Layout>
);

interface MainProps {
  environment: Environment;
  formState: Q.StateQueryResp;
  initialValues: Q.FormValues;
  onSubmit: (values: Q.FormValues) => void;
  onChange: (values: Q.FormValues) => void;
  user: User;
}

const MainForm: React.FC<MainProps> = (props) => (
  <Layout>
    <MaybeAdminControls user={props.user} stateQuery={props.formState} environment={props.environment} />
    <Wizard.Wizard initialValues={props.initialValues} onSubmit={props.onSubmit} onChange={props.onChange}>
      {props.environment === 'COUNSELLING' ? (
        <Wizard.Page>
          <h2 className="title">OPISKELUN TAITOKARTTA -kysely</h2>

          <p className="block">
            Opiskelun Taitokartta on osa Jyväskylän yliopiston Student Life -toimintaa ja Laadukkaan ohjauksen
            periaatteita (Laadukkaan ohjauksen periaatteet 2016). Se tukee opiskeluhyvinvointia auttamalla opiskelijoita
            tunnistamaan ja kehittämään omia akateemisia opiskelutaitojaan.
          </p>

          <p className="block">
            Opiskelun Taitokarttaa on kehitetty Jyväskylän yliopistossa vuodesta 2012 alkaen (Irma Kakkuri, Marjaana
            Häkkinen ja Jaana Ronkainen). Teoreettisena lähtökohtana lukemisessa on sanantunnistamisesta tekstin
            ymmärtämiseen ja sanan kirjoittamisesta tekstin tuottamiseen laajenevat mallit (Perfetti, C. 1999. Perfetti,
            C. & Stafura, J. 2014; Bereiter, C., Scardamalia, M. 1987; Weinsteinin 1987; Weinstein, Palmer & Shulte,
            2002), joita on täydennetty näiden taitojen haasteiden itsearvioinnilla (Lyon, G.R., Shaywitz,S.E.&
            Shaywitz, B.A. 2003 Lyytinen & Leinonen 1997, Leinonen, Müller, Leppänen, Aro, Ahonen & Lyytinen 1999) ja
            digitaalisen tekstin lukemisen ydintaidoilla (Kanniainen, L., Kiili, C., Tolvanen, A., Aro, M., & Leppänen,
            P. H. (2019). Kehittämisvaiheessa kerätyn empiirisen aineiston analyysin perusteella on muodostettu
            faktorimalli, jonka pohjalta mittarin väittämät on jäsennetty opiskelun ydintekstitaidoiksi ja opiskelun
            hallinnan taidoiksi. Opiskelun hallinnan taitoja arvioidaan laajemmin uusilla väittämillä, jotka on mukailtu
            käyttäen pohjana MSLQ-mittaria (Pintrich 1991) ja kokemuksellisen oppimisen ja minäpystyvyyden perusteita
            (Malinen 2014; Bandura 1997).
          </p>

          <p className="block">
            Opiskelun Taitokartta -kyselyyn vastaaminen auttaa sinua tunnistamaan akateemisten opiskelutaitojesi
            vahvuuksia ja kehittämistarpeita.
          </p>

          <p className="block">
            Huomioi, että saat automaattisen palautteen vasta kyselyn lopussa olevan LÄHETÄ-napin painalluksen jälkeen!
          </p>
        </Wizard.Page>
      ) : (
        <Wizard.Page>
          <h2 className="title">OPISKELUN TAITOKARTTA -kysely</h2>
          <p className="block">
            Pyydämme sinua vastaamaan oheiseen akateemisten opiskelutaitojen itsearviointiin, joka lähetetään kaikille
            1.vuoden opiskelijoille sekä pelkästään maisteriopinnot aloittaneille opiskelijoille osana ryhmänohjausta.
          </p>

          <p className="block">
            Pyydämme Sinua vastaamaan itsearviointiin mahdollisimman pian, sillä se on avoinna vain kuukauden.
          </p>

          <p className="block">
            Opiskelun Taitokartta on osa Jyväskylän yliopiston Student Life -toimintaa ja Laadukkaan ohjauksen
            periaatteita (Laadukkaan ohjauksen periaatteet 2016). Se tukee opiskeluhyvinvointia auttamalla opiskelijoita
            tunnistamaan ja kehittämään omia akateemisia opiskelutaitojaan.
          </p>

          <p className="block">
            Opiskelun Taitokarttaa on kehitetty Jyväskylän yliopistossa vuodesta 2012 alkaen (Irma Kakkuri, Marjaana
            Häkkinen ja Jaana Ronkainen). Teoreettisena lähtökohtana lukemisessa on sanantunnistamisesta tekstin
            ymmärtämiseen ja sanan kirjoittamisesta tekstin tuottamiseen laajenevat mallit (Perfetti 1999. Perfetti &
            Stafura 2014; Bereiter, Scardamalia 1987; Weinsteinin 1987; Weinstein, Palmer & Shulte, 2002), joita on
            täydennetty näiden taitojen haasteiden itsearvioinnilla (Lyon, Shaywitz & Shaywitz 2003 Lyytinen & Leinonen
            1997, Leinonen, Müller, Leppänen, Aro, Ahonen & Lyytinen 1999) ja digitaalisen tekstin lukemisen
            ydintaidoilla (Kanniainen, Kiili, Tolvanen, Aro, & Leppänen 2019). Kehittämisvaiheessa kerätyn empiirisen
            aineiston analyysin perusteella on muodostettu faktorimalli, jonka pohjalta mittarin väittämät on jäsennetty
            opiskelun ydintekstitaidoiksi ja opiskelun hallinnan taidoiksi. Opiskelun hallinnan taitoja arvioidaan
            laajemmin uusilla väittämillä, jotka on mukailtu käyttäen pohjana MSLQ-mittaria (Pintrich 1991) ja
            kokemuksellisen oppimisen ja minäpystyvyyden perusteita (Malinen 2014; Bandura 1997)
          </p>

          <p className="block">
            Tutkimusta Opiskelun Taitokartasta toteutetaan edelleen Jyväskylän yliopistossa. Siksi sinulta kysytään
            ennen varsinaisen Opiskelun Taitokartta -kyselyn alkua suostumusta käyttää antamiasi vastauksia
            tutkimukseen. Antamasi tiedot ovat käytössä vain niihin tarkoituksiin, joihin sinä annat suostumuksen.
            Opiskelun Taitokartan tutkimuksesta on tehty tieteellisen tutkimuksen tietosuojaseloste, josta ilmenevät
            tiedot tutkimusrekisterin rekisterinpitäjästä, henkilötietojen käsittelijöistä ja tietojen suojaamisesta.
            Voit tutustua tietosuojaselosteeseen täältä:{' '}
            <a href="https://www.jyu.fi/fi/palvelut/studentlife/sl-opiskelijalle/opiskelun-taitokartta/tietosuojailmoitus_opiskelun_taitokartta.pdf">
              Tietosuojaselosteeseen
            </a>
          </p>

          <p className="block">
            Huomioi, että vastaustesi tiedot tallentuvat vasta kyselyn lopussa olevan LÄHETÄ-napin painalluksen jälkeen!
          </p>
        </Wizard.Page>
      )}

      {/* permission questions reordered and pruned as of fall 2022.
      Only shown if data collection is active */}
      {props.environment === 'RESEARCH' && (
        <Wizard.Page>
          <h2 className="subtitle">Suostumus tutkimukseen</h2>
          <div className="message is-primary">
            <div className="message-body">
              <p className="block">
                1. Suostun Opiskelun Taitokartan itsearviointivastausteni hyödyntämiseen yliopisto-opetuksen ja
                ohjauksen tutkimusperustaisessa kehittämisessä. Nämä vastaukset eivät sisällä mitään tunnistetietoja.
              </p>
              <div className="columns is-centered">
                <div className="column is-half">
                  <PermissionRadio name="permission3" />
                </div>
              </div>
            </div>
          </div>
        </Wizard.Page>
      )}

      {props.environment === 'RESEARCH' && (
        <Wizard.Page>
          <h2 className="title">Suostumus tutkimukseen</h2>
          <div className="message is-primary">
            <div className="message-body">
              <p className="block">
                2. Annan suostumukseni yhdistää Jyväskylän yliopistossa olevia minua koskevia opiskelijarekisteritietoja
                (nimi, ikä, sukupuoli, tiedekunta, pääaine, opintopistemäärä, arvosanat) taustatietona Opiskelun
                taitokartan itsearviointiin antamiini vastauksiin.
              </p>
              <div className="columns is-centered">
                <div className="column is-half">
                  <PermissionRadio name="permission1" />
                </div>
              </div>
              <p className="mb-4">
                Nämä tiedot haetaan kirjautuneen käyttäjän perusteella. Olet kirjautunut henkilönä:
              </p>
              <label className="label">
                <span>Nimi&nbsp;</span>
                <Q.Field name="userFullName" component="input" type="text" className="input" disabled />
              </label>
              <label className="label">
                <span>Sähköposti&nbsp;</span>
                <Q.Field name="userEmail" component="input" type="text" className="input" disabled />
              </label>
            </div>
          </div>
        </Wizard.Page>
      )}

      {props.environment === 'RESEARCH' && (
        <Wizard.Page>
          <h2 className="subtitle">Suostumus tutkimukseen</h2>
          <div className="message is-primary">
            <div className="message-body">
              <p className="block">
                3. Suostun siihen, että aineistoa säilytetään tunnistetiedoin viisi vuotta opiskelutaitojeni
                kehittymisen seuraamiseen. Tämän jälkeen aineistoa säilytetään tunnistekoodein (nelinumeroinen luku)
                enintään 10 vuotta. Tämän jälkeen aineisto arkistoidaan ilman mitään tunnistetietoja.
              </p>
              <div className="columns is-centered">
                <div className="column is-half">
                  <PermissionRadio name="permission5" />
                </div>
              </div>
            </div>
          </div>
        </Wizard.Page>
      )}

      {props.environment === 'RESEARCH' && (
        <Wizard.Page>
          <h2 className="title">Taustatiedot</h2>
          <div className="columns">
            <div className="column is-half">
              <div className="field">
                <label className="label">Ikä</label>
                <div className="control">
                  <Validation name="age" validate={vRequired}>
                    <Q.Field name="age" component="input" type="number" className="input" />
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Sukupuoli</label>
                <div className="control">
                  <Validation name="gender" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="gender" component="input" type="radio" value="m" />
                      &nbsp;mies
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="gender" component="input" type="radio" value="f" />
                      &nbsp;nainen
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="gender" component="input" type="radio" value="o" />
                      &nbsp;muu
                    </label>
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Äidinkieli</label>
                <div className="control">
                  <Validation name="lang" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="suomi" />
                      &nbsp;suomi
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="ruotsi" />
                      &nbsp;ruotsi
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="englanti" />
                      &nbsp;englanti
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="venäjä" />
                      &nbsp;venäjä
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="saksa" />
                      &nbsp;saksa
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="ranska" />
                      &nbsp;ranska
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="espanja" />
                      &nbsp;espanja
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="italia" />
                      &nbsp;italia
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="lang" component="input" type="radio" value="other" />
                      &nbsp;muu, mikä:&nbsp;
                    </label>
                    <Q.Field
                      maxLength={20}
                      className="input is-inline"
                      name="langOther"
                      component="input"
                      type="text"
                    />
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Missä olet opiskellut aikaisemmin? Voit valita useamman vaihtoehdon.</label>
                <div className="control">
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="ao" />
                    &nbsp;ammatillinen&nbsp;koulutus
                  </label>
                  <br />
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="lukio" />
                    &nbsp;lukio
                  </label>
                  <br />
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="amk" />
                    &nbsp;ammattikorkeakoulu
                  </label>
                  <br />
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="avoinamk" />
                    &nbsp;avoin&nbsp;ammattikorkeakoulu
                  </label>
                  <br />
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="yo" />
                    &nbsp;yliopisto
                  </label>
                  <br />
                  <label className="radio">
                    <Q.Field name="studyBackground" component="input" type="checkbox" value="avoinyo" />
                    &nbsp;avoin&nbsp;yliopisto
                  </label>
                </div>
              </div>
              <div className="field">
                <label className="label">Opinto-oikeus</label>
                <div className="control">
                  <Validation name="studyRight" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="studyRight" component="input" type="radio" value="varsinainen" />
                      &nbsp;varsinainen
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="studyRight" component="input" type="radio" value="erillinen" />
                      &nbsp;erillinen
                    </label>
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Tiedekunta</label>
                <div className="control">
                  <Validation name="faculty" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="hytk" />
                      &nbsp;Humanistis-yhteiskuntatieteellinen tiedekunta
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="itk" />
                      &nbsp;Informaatioteknologian tiedekunta
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="jsbe" />
                      &nbsp;Kauppakorkeakoulu
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="edupsy" />
                      &nbsp;Kasvatustieteiden ja psykologian tiedekunta
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="ltk" />
                      &nbsp;Liikuntatieteellinen tiedekunta
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="faculty" component="input" type="radio" value="mltk" />
                      &nbsp;Matemaattis-luonnontieteellinen tiedekunta
                    </label>
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Tutkinto-ohjelma (pääaine)</label>
                <div className="control">
                  <Validation name="major" validate={vRequired}>
                    <Q.Field maxLength={25} className="input" name="major" component="input" type="text" />
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Opintojen alkamisvuosi</label>
                <div className="control">
                  <Validation name="beginYear" validate={vRequired}>
                    <Q.Field className="input" name="beginYear" component="input" type="number" />
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">
                  Opintojen vaihe tutkinto-ohjelmassa (pääaineessa), voit valita useamman vaihtoehdon
                </label>
                <div className="control">
                  <Validation name="studyLevel" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="studyLevel" component="input" type="checkbox" value="p" />
                      &nbsp;perusopinnot
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="studyLevel" component="input" type="checkbox" value="a" />
                      &nbsp;aineopinnot
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="studyLevel" component="input" type="checkbox" value="s" />
                      &nbsp;syventävät&nbsp;opinnot
                    </label>
                  </Validation>
                </div>
              </div>
              <div className="field">
                <label className="label">Suoritettavat opintoni</label>
                <div className="control">
                  <Validation name="degreeProgram" validate={vRequired}>
                    <label className="radio">
                      <Q.Field name="degreeProgram" component="input" type="radio" value="kandimaisteri" />
                      &nbsp;opiskelen sekä kandi- että maisteriopintoja Jyväskylän yliopistossa
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="degreeProgram" component="input" type="radio" value="maisteri" />
                      &nbsp;opiskelen vain maisteriopintoja Jyväskylän yliopistossa
                    </label>
                    <br />
                    <label className="radio">
                      <Q.Field name="degreeProgram" component="input" type="radio" value="chydenius" />
                      &nbsp;opiskelen Kokkolan yliopistokeskus Chydeniuksessa
                    </label>
                  </Validation>
                </div>
              </div>
            </div>
          </div>
        </Wizard.Page>
      )}

      <Wizard.Page>
        <h2 className="title">Lukemisen ydintaidot suomeksi ja englanniksi</h2>
        <div className="message is-primary">
          <p className="message-body">Vastaa seuraaviin väittämiin ja arvioi, kuinka hyvin väittämä sopii sinuun.</p>
        </div>
        <div className="container">
          {Object.keys(Q.basicReadingLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
          {Object.keys(Q.readingLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
          {Object.keys(Q.englishlikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
        </div>
      </Wizard.Page>

      <Wizard.Page>
        <h2 className="title">Digitaalinen lukeminen</h2>
        <div className="message is-primary">
          <p className="message-body">Vastaa seuraaviin väittämiin ja arvioi, kuinka hyvin väittämä sopii sinuun.</p>
        </div>
        <div className="container">
          {Object.keys(Q.digitalLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
        </div>
      </Wizard.Page>

      <Wizard.Page>
        <h2 className="title">Sanojen ja tekstin kirjoittamisen ydintaidot</h2>
        <div className="message is-primary">
          <p className="message-body">Vastaa seuraaviin väittämiin ja arvioi, kuinka hyvin väittämä sopii sinuun.</p>
        </div>
        <div className="container">
          {Object.keys(Q.basicWritingLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
          {Object.keys(Q.writingLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
          {Object.keys(Q.attitudeLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
        </div>
      </Wizard.Page>

      <Wizard.Page>
        <h2 className="title">Opiskelun hallinta ja ajankäytön suunnitelmallisuus</h2>
        <div className="message is-primary">
          <p className="message-body">Vastaa seuraaviin väittämiin ja arvioi, kuinka hyvin väittämä sopii sinuun.</p>
        </div>
        <div className="container">
          {Object.keys(Q.strategyLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
        </div>
      </Wizard.Page>

      <Wizard.Page>
        <h2 className="title">Opiskelutavat ja opiskelukokemukset</h2>
        <div className="message is-primary">
          <p className="message-body">Vastaa seuraaviin väittämiin ja arvioi, kuinka hyvin väittämä sopii sinuun.</p>
        </div>
        <div className="container">
          {Object.keys(Q.experienceLikerts).map((k) => (
            <Likert name={k as Q.LikertName} key={k} />
          ))}
        </div>
      </Wizard.Page>

      {props.environment === 'RESEARCH' && (
        <Wizard.Page>
          <h2 className="title">Vapaa sana</h2>
          <div className="message is-primary">
            <p className="message-body">
              Tähän voit kuvailla halutessasi tarkemmin oppimisen haasteita ja kertoa, millaisesta ohjauksesta arvelisit
              hyötyväsi.
            </p>
          </div>
          <Q.Field name="freeFeedback" component="textarea" maxLength={200} className="textarea" />
          <p className="mt-4">
            Huomioi, että kaikkien vastaustesi tiedot (niin suostumuskyselyn kuin myös Opiskelun Taitokartta
            -kyselynkin) sekä henkilötietosi tallentuvat alla olevan LÄHETÄ-napin painalluksen jälkeen!
          </p>
        </Wizard.Page>
      )}
    </Wizard.Wizard>
  </Layout>
);

export default App;
